import confetti from 'canvas-confetti';

type ConfettiOptions = {
  particleCount?: number;
  spread?: number;
  startVelocity?: number;
  decay?: number;
  scalar?: number;
  origin?: { x: number; y: number };
  colors?: string[];
  shapes?: ('square' | 'circle')[];
  gravity?: number;
  drift?: number;
  ticks?: number;
};

// Basic confetti burst
export const fireConfetti = (options?: ConfettiOptions) => {
  const defaults: ConfettiOptions = {
    particleCount: 100,
    spread: 70,
    startVelocity: 30,
    decay: 0.95,
    scalar: 1,
    origin: { x: 0.5, y: 0.5 },
    colors: ['#8B5CF6', '#D946EF', '#06B6D4', '#10B981', '#F59E0B'],
    shapes: ['square', 'circle'],
    gravity: 1,
    ticks: 200,
  };

  confetti({
    ...defaults,
    ...options,
  });
};

// Success celebration (from bottom center)
export const fireSuccessConfetti = () => {
  const count = 200;
  const defaults = {
    origin: { y: 0.7 },
    colors: ['#10B981', '#34D399', '#6EE7B7', '#A7F3D0'],
  };

  function fire(particleRatio: number, opts: ConfettiOptions) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio),
    });
  }

  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });
};

// Celebration burst from both sides
export const fireCelebration = () => {
  const duration = 3000;
  const animationEnd = Date.now() + duration;
  const colors = ['#8B5CF6', '#D946EF', '#06B6D4', '#10B981'];

  const randomInRange = (min: number, max: number) => {
    return Math.random() * (max - min) + min;
  };

  const interval = setInterval(() => {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      clearInterval(interval);
      return;
    }

    const particleCount = 50 * (timeLeft / duration);

    // Left side
    confetti({
      particleCount,
      startVelocity: 30,
      spread: 360,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      colors,
    });

    // Right side
    confetti({
      particleCount,
      startVelocity: 30,
      spread: 360,
      origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      colors,
    });
  }, 250);
};

// Star-shaped burst
export const fireStars = () => {
  const defaults = {
    spread: 360,
    ticks: 100,
    gravity: 0,
    decay: 0.94,
    startVelocity: 20,
    colors: ['#FFD700', '#FFA500', '#FF6347', '#FF4500'],
    shapes: ['circle'] as ('circle')[],
  };

  function shoot() {
    confetti({
      ...defaults,
      particleCount: 30,
      scalar: 1.2,
      shapes: ['circle'],
    });

    confetti({
      ...defaults,
      particleCount: 20,
      scalar: 0.75,
      shapes: ['circle'],
    });
  }

  setTimeout(shoot, 0);
  setTimeout(shoot, 100);
  setTimeout(shoot, 200);
};

// Emoji rain (for special occasions)
export const fireEmoji = (emoji: string = '🎉') => {
  const scalar = 2;
  const unicorn = confetti.shapeFromText({ text: emoji, scalar });

  const defaults = {
    spread: 360,
    ticks: 60,
    gravity: 0.5,
    decay: 0.96,
    startVelocity: 20,
    shapes: [unicorn],
    scalar,
  };

  function shoot() {
    confetti({
      ...defaults,
      particleCount: 30,
    });

    confetti({
      ...defaults,
      particleCount: 5,
      flat: true,
    });
  }

  setTimeout(shoot, 0);
  setTimeout(shoot, 100);
  setTimeout(shoot, 200);
};

// Quick subtle burst (for small wins)
export const fireQuickBurst = (x: number, y: number) => {
  confetti({
    particleCount: 30,
    spread: 60,
    startVelocity: 20,
    decay: 0.95,
    origin: { x, y },
    colors: ['#8B5CF6', '#D946EF', '#06B6D4'],
    scalar: 0.8,
    ticks: 100,
  });
};
